import type { GridData, Region, RegionMap, RegionShard, Shard, ShardMap } from '../lib/types'
import { createEmptyGrid, getGridPixel, updateGridPixel } from '../grid/utils'
import { getRandomShard } from '.'

const logs = false
function log(message: string) {
  if (logs) {
    console.log(message)
  }
}

interface ShardPlacementOptions {
  blockSize: number
  minShardsPerRegion?: number
  maxShardsPerRegion?: number
}

/**
 * Places shards on the grid with configurable distribution.
 */
export function placeShardsOnGrid(
  grid: GridData,
  regionMap: RegionMap,
  options: ShardPlacementOptions,
): { grid: GridData; occupiedGrid: boolean[][] } {
  const { width, height } = grid
  const { blockSize, minShardsPerRegion = 1, maxShardsPerRegion = 4 } = options

  // Create result grid and tracking array
  const resultGrid = createEmptyGrid(width, height)
  const occupiedGrid: boolean[][] = Array(height)
    .fill(false)
    .map(() => Array(width).fill(false))

  // Copy current grid to result grid
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      updateGridPixel(resultGrid, x, y, { ...getGridPixel(grid, x, y) })
    }
  }

  // Process each region
  regionMap.regions.forEach((region) => {
    const numShards = Math.floor(
      Math.random() * (maxShardsPerRegion - minShardsPerRegion + 1) + minShardsPerRegion,
    )

    for (let i = 0; i < numShards; i++) {
      placeShardInRegion(region, resultGrid, occupiedGrid, blockSize)
    }
  })

  return { grid: resultGrid, occupiedGrid }
}

/**
 * Places a single shard within a region.
 */
function placeShardInRegion(
  region: Region,
  grid: GridData,
  occupiedGrid: boolean[][],
  blockSize: number,
): void {
  const shard = getRandomShard()

  // Calculate maximum possible offset
  const maxShardWidth = Math.max(...shard.blocks.map((b) => b.x)) + 1
  const maxShardHeight = Math.max(...shard.blocks.map((b) => b.y)) + 1

  const maxOffsetX = Math.max(0, region.width - maxShardWidth * blockSize)
  const maxOffsetY = Math.max(0, region.height - maxShardHeight * blockSize)

  // Try to find a valid position
  let validPosition = false
  let attempts = 0
  const maxAttempts = 100

  while (!validPosition && attempts < maxAttempts) {
    const offsetX = Math.floor(Math.random() * maxOffsetX)
    const offsetY = Math.floor(Math.random() * maxOffsetY)

    if (isPositionValid(region, shard, offsetX, offsetY, occupiedGrid, blockSize)) {
      placeShardAtPosition(region, shard, offsetX, offsetY, grid, occupiedGrid, blockSize)
      validPosition = true
    }

    attempts++
  }
}

/**
 * Applies shards to the grid
 */
export function applyShardsToGrid(
  grid: GridData,
  regionMap: RegionMap,
  shardMap: ShardMap,
): GridData {
  const { width, height } = grid
  const shardedGrid = createEmptyGrid(width, height)

  // First, copy the original grid to the sharded grid
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      updateGridPixel(shardedGrid, x, y, getGridPixel(grid, x, y))
    }
  }

  // Get region by id map for quick lookup
  const regionsById = new Map<number, Region>()
  regionMap.regions.forEach((region) => {
    regionsById.set(region.id, region)
  })

  // Apply each shard
  shardMap.shards.forEach((shard) => {
    const region = regionsById.get(shard.regionId)
    if (!region) return

    // Calculate the base position of the shard in the grid
    const baseX = region.destX + shard.offsetX * shard.size
    const baseY = region.destY + shard.offsetY * shard.size

    // Draw each block of the shard with its own color
    shard.blocks.forEach((block) => {
      // Get the color for this specific block
      const { r, g, b } = block.color

      const blockX = baseX + block.x * shard.size
      const blockY = baseY + block.y * shard.size

      // Fill the block with the shard color
      for (let y = 0; y < shard.size; y++) {
        for (let x = 0; x < shard.size; x++) {
          const gridX = blockX + x
          const gridY = blockY + y

          // Check bounds
          if (gridY >= 0 && gridY < height && gridX >= 0 && gridX < width) {
            updateGridPixel(shardedGrid, gridX, gridY, { r, g, b, a: 255 })
          }
        }
      }
    })
  })

  return shardedGrid
}

/**
 * Checks if a position is valid for placing a shard.
 */
function isPositionValid(
  region: Region,
  shardShape: { blocks: { x: number; y: number }[] },
  offsetX: number,
  offsetY: number,
  occupiedGrid: boolean[][],
  blockSize: number,
): boolean {
  const width = occupiedGrid[0]!.length
  const height = occupiedGrid.length

  for (const block of shardShape.blocks) {
    const blockX = region.x + offsetX + block.x * blockSize
    const blockY = region.y + offsetY + block.y * blockSize

    // Check bounds
    if (blockY < 0 || blockY >= height || blockX < 0 || blockX >= width) {
      return false
    }

    // Check if position is already occupied
    if (occupiedGrid[blockY]![blockX]) {
      return false
    }
  }

  return true
}

/**
 * Places a shard at a specific position.
 */
function placeShardAtPosition(
  region: Region,
  shard: Shard,
  offsetX: number,
  offsetY: number,
  grid: GridData,
  occupiedGrid: boolean[][],
  blockSize: number,
): void {
  const { width, height } = grid

  shard.blocks.forEach((block) => {
    const blockX = region.x + offsetX + block.x * blockSize
    const blockY = region.y + offsetY + block.y * blockSize
    const { r, g, b } = block.color!

    // Fill the block with the assigned color
    for (let y = 0; y < blockSize; y++) {
      for (let x = 0; x < blockSize; x++) {
        const pixelX = blockX + x
        const pixelY = blockY + y

        if (pixelY >= 0 && pixelY < height && pixelX >= 0 && pixelX < width) {
          updateGridPixel(grid, pixelX, pixelY, { r, g, b, a: 255 })
          occupiedGrid[pixelY]![pixelX] = true
        }
      }
    }
  })
}

/**
 * Creates shards for regions with cross-region collision detection
 */
export function createShardMap(regionMap: RegionMap, blockSize: number): ShardMap {
  const { regions } = regionMap
  const shards: RegionShard[] = []
  let shardId = 0

  // Create a global occupancy grid for the entire image
  // First, determine the total dimensions
  let maxX = 0
  let maxY = 0

  regions.forEach((region) => {
    const regionEndX = region.destX + region.width
    const regionEndY = region.destY + region.height
    maxX = Math.max(maxX, regionEndX)
    maxY = Math.max(maxY, regionEndY)
  })

  // Create the global occupancy grid (in block units)
  const gridWidthInUnits = Math.ceil(maxX / blockSize)
  const gridHeightInUnits = Math.ceil(maxY / blockSize)

  log(`Creating global occupancy grid: ${gridWidthInUnits}x${gridHeightInUnits} units`)

  // Initialize the global occupancy grid (0 = free, 1 = occupied, 2 = adjacent to occupied)
  const globalOccupancyGrid: number[][] = Array(gridHeightInUnits)
    .fill(0)
    .map(() => Array(gridWidthInUnits).fill(0))

  // Process regions in a random order to avoid bias
  const shuffledRegions = [...regions].sort(() => Math.random() - 0.5)

  shuffledRegions.forEach((region) => {
    log(`Processing region ${region.id} at dest (${region.destX}, ${region.destY})`)

    // Create exactly 4 shards per region
    for (let i = 0; i < 4; i++) {
      // Select a random shard shape
      const shard = getRandomShard()

      // Calculate the maximum possible offset to ensure shard stays within region
      const maxShardWidth = Math.max(...shard.blocks.map((b) => b.x)) + 1
      const maxShardHeight = Math.max(...shard.blocks.map((b) => b.y)) + 1

      const regionWidthInUnits = Math.floor(region.width / blockSize)
      const regionHeightInUnits = Math.floor(region.height / blockSize)

      const maxOffsetX = Math.max(0, regionWidthInUnits - maxShardWidth)
      const maxOffsetY = Math.max(0, regionHeightInUnits - maxShardHeight)

      // Try to find a valid position (max 100 attempts)
      let validPosition = false
      let offsetX = 0
      let offsetY = 0
      let attempts = 0
      const maxAttempts = 100

      while (!validPosition && attempts < maxAttempts) {
        // Calculate random offset (in pixel units)
        offsetX = Math.floor(Math.random() * (maxOffsetX + 1))
        offsetY = Math.floor(Math.random() * (maxOffsetY + 1))

        // Convert region-relative position to global grid position
        const globalOffsetX = Math.floor(region.destX / blockSize) + offsetX
        const globalOffsetY = Math.floor(region.destY / blockSize) + offsetY

        // Check if this position is valid (no collisions)
        validPosition = true

        // Check each block of the shard
        for (const block of shard.blocks) {
          const blockX = globalOffsetX + block.x
          const blockY = globalOffsetY + block.y

          // Check if this block position is already occupied or out of bounds
          if (
            blockY < 0 ||
            blockY >= gridHeightInUnits ||
            blockX < 0 ||
            blockX >= gridWidthInUnits ||
            globalOccupancyGrid[blockY]![blockX]! > 0
          ) {
            validPosition = false
            break
          }

          // Check all adjacent cells (including diagonals) to ensure shards don't touch
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue // Skip the block itself

              const adjX = blockX + dx
              const adjY = blockY + dy

              // Check if adjacent cell is within bounds and occupied
              if (
                adjY >= 0 &&
                adjY < gridHeightInUnits &&
                adjX >= 0 &&
                adjX < gridWidthInUnits &&
                globalOccupancyGrid[adjY]![adjX] === 1
              ) {
                validPosition = false
                break
              }
            }
            if (!validPosition) break
          }
          if (!validPosition) break
        }

        attempts++
      }

      if (validPosition) {
        log(`Found valid position for shard ${i} in region ${region.id} after ${attempts} attempts`)

        // Convert region-relative position to global grid position
        const globalOffsetX = Math.floor(region.destX / blockSize) + offsetX
        const globalOffsetY = Math.floor(region.destY / blockSize) + offsetY

        // Mark cells as occupied in the global grid
        for (const block of shard.blocks) {
          const blockX = globalOffsetX + block.x
          const blockY = globalOffsetY + block.y

          globalOccupancyGrid[blockY]![blockX] = 1 // Mark as occupied

          // Mark adjacent cells (including diagonals) as "adjacent to occupied"
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue // Skip the block itself

              const adjX = blockX + dx
              const adjY = blockY + dy

              // Check if adjacent cell is within bounds
              if (
                adjY >= 0 &&
                adjY < gridHeightInUnits &&
                adjX >= 0 &&
                adjX < gridWidthInUnits &&
                globalOccupancyGrid[adjY]![adjX] === 0
              ) {
                globalOccupancyGrid[adjY]![adjX] = 2 // Mark as adjacent to occupied
              }
            }
          }
        }

        // Create a shard
        shards.push({
          ...shard,
          regionId: region.id,
          offsetX,
          offsetY,
          size: blockSize,
          id: shardId++,
        })
      } else {
        log(
          `Couldn't find valid position for shard ${i} in region ${region.id} after ${maxAttempts} attempts`,
        )

        // Skip this shard if we can't find a valid position
        // This is better than placing overlapping shards
        continue
      }
    }
  })

  return { shards }
}
